import React, { useEffect, useState } from "react";
import { API } from "@/lib/constants";
import { ShieldCheck, Loader2, CheckCircle2, XCircle, MinusCircle, Clock, RefreshCw } from "lucide-react";

// ITEM 20c — the PANEL. Looking is not being told; the telling is the email. But a person should be
// able to look, and a quiet panel must never be mistaken for a healthy one (staleness is shown).
const STATE = {
  ok: { icon: CheckCircle2, cls: "text-emerald-600", label: "Working" },
  down: { icon: XCircle, cls: "text-red-600", label: "Down" },
  not_configured: { icon: MinusCircle, cls: "text-slate-400", label: "Not configured" },
  unknown: { icon: MinusCircle, cls: "text-slate-400", label: "Unknown" },
};

export default function IntegrationHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const load = () => {
    setLoading(true);
    fetch(`${API}/admin/integration-health`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(load, []);

  const runCheck = async () => {
    setChecking(true);
    await fetch(`${API}/admin/integration-health/check`, { method: "POST", credentials: "include" }).catch(() => {});
    setChecking(false);
    load();
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-8" data-testid="integration-health-page">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-lg bg-slate-100 p-2 text-slate-600"><ShieldCheck className="h-5 w-5" /></div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Integration health</h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">
              Everything Rex reaches outside itself. You are <span className="font-medium text-slate-700">told by email</span> when
              one changes — this page is just for looking.
            </p>
          </div>
        </div>
        <button onClick={runCheck} disabled={checking} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50" data-testid="integration-health-recheck">
          {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-check now
        </button>
      </div>

      {loading && <div className="mt-10 flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Checking…</div>}

      {!loading && data && (
        <>
          <div className={`mt-5 flex items-center gap-2 rounded-lg px-4 py-2.5 text-xs ${data.stale ? "border border-amber-200 bg-amber-50 text-amber-800" : "border border-slate-200 bg-slate-50 text-slate-500"}`} data-testid="integration-health-staleness">
            <Clock className="h-3.5 w-3.5" /> {data.staleness_note}
          </div>

          <div className="mt-6 space-y-3" data-testid="integration-health-list">
            {data.integrations.map((it) => {
              const s = STATE[it.state] || STATE.unknown;
              const Icon = s.icon;
              return (
                <div key={it.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`integration-${it.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-base font-semibold text-slate-900">{it.label}</span>
                    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${s.cls}`} data-testid={`integration-${it.id}-state`}>
                      <Icon className="h-4 w-4" /> {s.label}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-slate-600">{it.used_for}</p>
                  {it.state === "down" && (
                    <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800" data-testid={`integration-${it.id}-degraded`}>
                      {it.degraded}{it.detail ? <span className="text-red-500"> — {it.detail}</span> : null}
                    </p>
                  )}
                  {it.state === "not_configured" && (
                    <p className="mt-1 text-xs text-slate-400">{it.detail}</p>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
